import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { ACTIVE_CHAT_STATUSES, useChatStore } from './useChatStore'
import { ChatStatus } from '../types/enums'

const DRAFTS_STORAGE_PREFIX = 'whatsapp_chat_drafts:'

// El borrador vive mientras su chat siga en la lista y admita un mensaje del
// agente. Quedan fuera los dos estados en los que el chat sigue listado pero el
// texto ya no se puede enviar: EXPIRE (ventana de 24 h vencida) y TEMPLATE
// (plantilla enviada, esperando que el cliente conteste).
const DRAFT_MUTE_STATUSES: ChatStatus[] = [ChatStatus.EXPIRE, ChatStatus.TEMPLATE]

const DRAFT_KEEPING_STATUSES: ChatStatus[] = ACTIVE_CHAT_STATUSES.filter(
  (status) => !DRAFT_MUTE_STATUSES.includes(status),
)

// Tope de seguridad del almacenamiento: si se acumulan mas, se conservan los
// mas recientes.
const MAX_STORED_DRAFTS = 100

// Escribir en cada tecla serializaria el mapa entero. El estado en memoria ya
// sobrevive al cambio de chat y de vista; localStorage solo cubre la recarga.
const PERSIST_DEBOUNCE_MS = 400

interface ChatDraft {
  text: string
  updatedAt: number
}

/**
 * Lo que el agente lleva escrito y todavia no envio, por chat.
 *
 * Vive fuera de los componentes del chat a proposito: `ChatView` y su pie se
 * desmontan al salir de la vista de WhatsApp (no hay `keep-alive` en las rutas)
 * y el editor se vaciaba. Aqui el texto sobrevive al cambio de chat, al cambio
 * de vista y —via localStorage, por agente— a la recarga del navegador.
 *
 * Lo unico que mantiene vivo un borrador es no haberse enviado: al enviarlo se
 * borra, y al cerrarse el chat tambien (ya no hay donde mandarlo).
 */
export const useChatDraftStore = defineStore('whatsappChatDraft', () => {
  // El mismo origen de usuario que usa el chat store para sus claves por
  // agente; el de auth arrastra servicios que aqui no pintan nada.
  const chatStore = useChatStore()
  const drafts = ref<Record<string, ChatDraft>>({})
  let persistTimer: ReturnType<typeof setTimeout> | null = null

  const storageKeyFor = (userId: string | null) =>
    `${DRAFTS_STORAGE_PREFIX}${userId || 'anonymous'}`

  const storageKey = () => storageKeyFor(chatStore.currentUserId)

  const prunedEntries = (entries: Record<string, ChatDraft>): Record<string, ChatDraft> =>
    Object.fromEntries(
      Object.entries(entries)
        .filter(([chatId, draft]) => chatId && draft.text.trim())
        .sort(([, a], [, b]) => b.updatedAt - a.updatedAt)
        .slice(0, MAX_STORED_DRAFTS),
    )

  const loadDrafts = () => {
    try {
      const stored = localStorage.getItem(storageKey())
      const parsed = stored ? JSON.parse(stored) : null

      if (!parsed || typeof parsed !== 'object') {
        drafts.value = {}
        return
      }

      drafts.value = prunedEntries(
        Object.fromEntries(
          Object.entries(parsed).flatMap(([chatId, draft]) => {
            if (!draft || typeof draft !== 'object') return []

            const candidate = draft as Partial<ChatDraft>
            if (typeof candidate.text !== 'string') return []

            return [
              [
                chatId,
                {
                  text: candidate.text,
                  updatedAt:
                    typeof candidate.updatedAt === 'number' ? candidate.updatedAt : Date.now(),
                },
              ],
            ]
          }),
        ),
      )
    } catch {
      drafts.value = {}
    }
  }

  const persistDrafts = (key = storageKey()) => {
    if (persistTimer) {
      clearTimeout(persistTimer)
      persistTimer = null
    }

    try {
      const entries = prunedEntries(drafts.value)

      if (Object.keys(entries).length) {
        localStorage.setItem(key, JSON.stringify(entries))
      } else {
        localStorage.removeItem(key)
      }
    } catch {
      // Sin almacenamiento el borrador dura lo que la pestana.
    }
  }

  const schedulePersist = () => {
    if (persistTimer) clearTimeout(persistTimer)
    // Envuelto por lo mismo que el listener de `pagehide`: `persistDrafts` toma
    // la clave como primer parametro y no puede recibirla de quien lo invoca.
    persistTimer = setTimeout(() => persistDrafts(), PERSIST_DEBOUNCE_MS)
  }

  const getDraft = (chatId: string): string => drafts.value[chatId]?.text ?? ''

  const hasDraft = (chatId: string): boolean => Boolean(getDraft(chatId).trim())

  const clearDraft = (chatId: string) => {
    if (!drafts.value[chatId]) return

    delete drafts.value[chatId]
    schedulePersist()
  }

  const setDraft = (chatId: string, text: string) => {
    if (!chatId) return

    // Solo espacios no es un borrador, y ademas `prunedEntries` no lo guardaria:
    // asi memoria y almacenamiento dicen lo mismo.
    if (!text.trim()) {
      clearDraft(chatId)
      return
    }

    if (drafts.value[chatId]?.text === text) return

    drafts.value[chatId] = { text, updatedAt: Date.now() }
    schedulePersist()
  }

  const clearAllDrafts = () => {
    drafts.value = {}
    persistDrafts()
  }

  const dropDraftsOutside = (liveChatIds: Set<string>) => {
    const staleChatIds = Object.keys(drafts.value).filter((chatId) => !liveChatIds.has(chatId))
    if (!staleChatIds.length) return

    staleChatIds.forEach((chatId) => delete drafts.value[chatId])
    schedulePersist()
  }

  // Solo lee `chat_id` y `status`, asi que no se recalcula con cada mensaje que
  // entra: unicamente cuando la lista cambia o un chat cambia de estado.
  const draftKeepingChatIds = computed(() => {
    const ids = new Set<string>()

    chatStore.chats.forEach((chat) => {
      if (DRAFT_KEEPING_STATUSES.includes(chat.status)) ids.add(chat.chat_id)
    })

    return ids
  })

  // Regla 1 — el chat estaba admitiendo borrador y dejo de hacerlo: se
  // finalizo, vencio, quedo esperando plantilla, o salio de la lista
  // (transferido, liberado, cerrado por inactividad). Se mira la desaparicion,
  // no la ausencia: las tandas de `loadChats` solo anaden, nunca quitan, asi
  // que lo que se cae de este conjunto se cayo de verdad.
  watch(draftKeepingChatIds, (chatIds, previousChatIds) => {
    // Con la lista vacia no hay nada que concluir: es el arranque, una
    // reconexion o el logout, y los chats siguen existiendo.
    if (!chatStore.chats.length) return

    previousChatIds.forEach((chatId) => {
      if (!chatIds.has(chatId)) clearDraft(chatId)
    })
  })

  // Regla 2 — los borradores que vienen de localStorage son de chats que esta
  // sesion quiza no ha visto nunca. Que no esten en la lista no basta (pueden
  // venir en una tanda posterior): hace falta la sincronizacion completa.
  watch(
    () => chatStore.fullSyncCount,
    () => dropDraftsOutside(draftKeepingChatIds.value),
  )

  // Cambiar de agente en el mismo navegador trae sus borradores, no los del
  // anterior. Lo que el saliente dejo en vuelo (debounce sin vencer) se escribe
  // en SU clave antes de cargar las del nuevo.
  watch(
    () => chatStore.currentUserId,
    (_userId, previousUserId) => {
      persistDrafts(storageKeyFor(previousUserId ?? null))
      loadDrafts()
    },
  )

  // Cerrar o recargar la pestana dentro de la ventana del debounce no puede
  // costar el ultimo tramo de lo escrito.
  if (typeof window !== 'undefined') {
    // Envuelto: `persistDrafts` recibe la clave, y el listener le pasaria el
    // evento.
    window.addEventListener('pagehide', () => persistDrafts())
  }

  loadDrafts()

  return {
    drafts,
    getDraft,
    hasDraft,
    setDraft,
    clearDraft,
    clearAllDrafts,
    persistDrafts,
  }
})
